import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";

import UserProfileModal from "../../screens/user/userProfile/UserProfileModal";
import { CommonContainer } from "../commonContainer/commonContainer";
import { ImageView } from "../ImageView/ImageView";
import ModalMenuBar from "../menuBar/ModalMenu";
import { HeaderMobileView } from "./HeaderMobileView";
import { HeaderWebView } from "./HeaderWebView";
import { HeaderMobileViewLegacy } from "./legacy/HeaderMobileViewLegacy";
import { HeaderWebViewLegacy } from "./legacy/HeaderWebViewLegacy";

import { useAuth } from "react-oidc-context";
import { useGetCompanySearch, useGetCompanyToken } from "../../api/company";
import { useGetNotification, useNotificationRead } from "../../api/notification";
import { usePostUserLogout } from "../../api/userDetails";
import { images } from "../../assets/images";
import defaultUserImage from "../../assets/images/default_profile_pic.jpeg";
import { getHeaderMenuItems } from "../../helpers/AuthorizationHelper";
import { errorView } from "../../helpers/ErrorHandler";
import { nameSort } from "../../helpers/NameSort";
import { useAuthContext } from "../../hooks/useAuthContext";
import { userRoles } from "../../routes/useRoleAccess";
import { getAccessToken } from "../../utils/authTokenStore";
import { getUserIdInLocal, saveCompanyTenantId, saveTenantId } from "../../utils/cacheStorage";

import { DISABLE_COGNITO_LOGIN, ENABLE_APP_SIDEBAR_REDESIGN } from "../../config/constants";
import { useCompanyContext } from "../../context/CompanyContext";
import "./Header.scss";

export const Header = memo(({ userDetails, setVisible, isLogin }: any) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { refreshCompanyList } = useCompanyContext();
  const { user, newNotification, dispatch, logout }: any = useAuthContext();

  // Legacy-only: drives the old ModalMenuBar hamburger menu. Only read/set
  // when ENABLE_APP_SIDEBAR_REDESIGN is false - the redesigned mobile header
  // opens the AppSidebar drawer instead.
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleUserProfile, setVisibleUserProfile] = useState(false);
  const [loadMore, setLoadMore] = useState(0);

  const baseOptions = useMemo(() => getHeaderMenuItems(navigate), [navigate]);
  const additionalOptions = useMemo(
    () => [
      {
        key: "profile",
        text: "Profile",
        value: "profile",
        onClick: () => setVisibleUserProfile(true)
      },
      { key: "logout", text: "Logout", value: "logout", onClick: () => userLogout() }
    ],
    []
  );
  const options = useMemo(
    () => [...baseOptions, ...additionalOptions],
    [baseOptions, additionalOptions]
  );

  const signOutRedirect = () => {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const logoutUri = process.env.REACT_APP_COGNITO_LOG_OUT_URI;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    auth.removeUser();
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  };

  const { isLoading: loadLogoutUser, mutate: userLogOut } = usePostUserLogout();
  const { mutate: updateIsReadNotification } = useNotificationRead();
  const {
    data: company,
    isLoading: loadCompanyData,
    mutate: searchCompany
  } = useGetCompanySearch();

  const {
    data: tokenData,
    isLoading: loadCompanyToken,
    mutate: getCompanyToken,
    isSuccess: isCompanyTokenSuccess
  } = useGetCompanyToken();

  const {
    isLoading: isLoadingNotification,
    data: notificationData,
    refetch
  } = useGetNotification(user, newNotification, loadMore);

  // Fetch company list initially
  useEffect(() => {
    if (getAccessToken()) {
      searchCompany("");
    }
  }, [searchCompany, refreshCompanyList]);

  // The server session now holds the new company (change-tenant sets the active tenant), so a
  // reload restores the session on it. Reloading also resets what is read once at mount: user
  // roles and permissions, context state and open connections.
  useEffect(() => {
    if (isCompanyTokenSuccess) {
      if (tokenData?.token) {
        window.location.reload();
      } else {
        errorView("Company loading issue");
      }
    }
  }, [isCompanyTokenSuccess, tokenData]);

  const getSearchValue = useCallback(
    (data: any) => {
      const tenantId = data?._id;
      const title = data?.name;
      const companyTenantId = data?.tenantId;
      if (getAccessToken()) {
        saveTenantId(companyTenantId);
        saveCompanyTenantId(companyTenantId);
        getCompanyToken(tenantId);
        dispatch({ type: "SET_COMPANY", payload: title });
        dispatch({ type: "SET_COMPANY_ID", payload: tenantId });
        navigate("/");
      }
    },
    [dispatch, getCompanyToken, navigate]
  );

  const logoutUserWithCognito = () => {
    if (DISABLE_COGNITO_LOGIN) return navigate("/auth");
    signOutRedirect();
  };

  const handleUserLogout = () => {
    logout();
    logoutUserWithCognito();
  };

  const userLogout = useCallback(() => {
    userLogOut("", {
      onSuccess: () => handleUserLogout(),
      // Keep the session when the server could not end it: the refresh cookie is httpOnly and
      // cannot be cleared from script, so signing out locally would sign the user back in on reload.
      onError: () => errorView("Logout failed, please try again")
    });
  }, [dispatch, navigate, userLogOut]);

  const onClickNotification = useCallback(
    (id: string) => {
      updateIsReadNotification(id, { onSuccess: () => refetch });
    },
    [updateIsReadNotification, refetch]
  );

  const renderUserImage = useMemo(() => {
    if (isLogin) return null;

    const profileImg = user?.profileUrl || defaultUserImage;

    return (
      <>
        <label className="userName">Hi, {nameSort(user?.firstName || "")}</label>
        {[profileImg, images?.DropDownIcon].map((src, idx) => (
          <ImageView
            key={idx}
            avatar
            imgOne={src}
            imgTwo={src}
            addRandomId={false}
            customImageView="userProfileTopHeader"
          />
        ))}
      </>
    );
  }, [isLogin, user]);

  if (loadCompanyToken || loadLogoutUser) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  // Previous (pre-redesign) header: Semantic UI web/mobile views plus the
  // ModalMenuBar hamburger menu. Left untouched behind the flag so turning
  // ENABLE_APP_SIDEBAR_REDESIGN off fully restores the old behaviour.
  if (!ENABLE_APP_SIDEBAR_REDESIGN) {
    return (
      <>
        <HeaderWebViewLegacy
          options={options}
          userImage={renderUserImage}
          navigate={navigate}
          userDetails={userDetails}
          company={company}
          getSearchValue={getSearchValue}
          loadCompanyData={loadCompanyData}
          loadCompanyToken={loadCompanyToken}
          searchCompany={searchCompany}
          isLoadingNotification={isLoadingNotification}
          notificationData={notificationData}
          updateLoadMore={(isListEmpty: boolean) => {
            setLoadMore(isListEmpty ? 0 : loadMore + 10);
          }}
          onClickNotification={onClickNotification}
        />
        <CommonContainer>
          <HeaderMobileViewLegacy
            options={options}
            userImage={renderUserImage}
            navigate={navigate}
            userDetails={userDetails}
            setVisible={setVisible}
            modalStatue={() => setVisibleModal(!visibleModal)}
            company={company}
            getSearchValue={getSearchValue}
            loadCompanyData={loadCompanyData}
            loadCompanyToken={loadCompanyToken}
            searchCompany={searchCompany}
          />
        </CommonContainer>
        <ModalMenuBar visibleModal={visibleModal} setVisibleModal={() => setVisibleModal(false)} />
        {!isLogin && (
          <UserProfileModal
            visibleModal={visibleUserProfile}
            setVisibleModal={() => setVisibleUserProfile(false)}
            id={getUserIdInLocal()}
            userRolesData={userRoles}
          />
        )}
      </>
    );
  }

  // New (ENABLE_APP_SIDEBAR_REDESIGN) header: Tailwind web view + AppSidebar
  // triggers instead of the ModalMenuBar.
  return (
    <>
      <HeaderWebView
        options={options}
        userImage={renderUserImage}
        navigate={navigate}
        userDetails={userDetails}
        company={company}
        getSearchValue={getSearchValue}
        loadCompanyData={loadCompanyData}
        loadCompanyToken={loadCompanyToken}
        searchCompany={searchCompany}
        isLoadingNotification={isLoadingNotification}
        notificationData={notificationData}
        updateLoadMore={(isListEmpty: boolean) => {
          setLoadMore(isListEmpty ? 0 : loadMore + 10);
        }}
        onClickNotification={onClickNotification}
        isLogin={isLogin}
      />
      <CommonContainer>
        <HeaderMobileView
          options={options}
          userImage={renderUserImage}
          navigate={navigate}
          userDetails={userDetails}
          setVisible={setVisible}
          isLogin={isLogin}
          company={company}
          getSearchValue={getSearchValue}
          loadCompanyData={loadCompanyData}
          loadCompanyToken={loadCompanyToken}
          searchCompany={searchCompany}
        />
      </CommonContainer>
      {!isLogin && (
        <UserProfileModal
          visibleModal={visibleUserProfile}
          setVisibleModal={() => setVisibleUserProfile(false)}
          id={getUserIdInLocal()}
          userRolesData={userRoles}
        />
      )}
    </>
  );
});
